import { Control, UseFormRegister } from 'react-hook-form'

import {
  BatchHistory,
  BatchStatus,
  BatchTrigger
} from '@/common/api/params/featurePageImage'
import {
  useFeaturePageImageHistoriesApi,
  useFeaturePageImageRunApi
} from '@/common/api/modules/featurePageImage'
import { registerNumberOption } from '@/common/form/register'
import { SiteFormSchemaType } from '@/components/container/form/modules/admin/site/siteSchema'
import FormCheckBox from '@/components/container/form/widgets/FormCheckBox'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

// 実行時刻は1時間単位。24択
const HOUR_CHOICES = Array.from({ length: 24 }, (_, h) => ({ name: `${h}時`, value: h }))

const TRIGGER_LABEL: Record<BatchTrigger, string> = {
  auto: '定時',
  manual: '手動'
}

const STATUS_LABEL: Record<BatchStatus, { label: string; badge: BadgeType }> = {
  complete: { label: '完了', badge: BadgeType.SUCCESS },
  partial: { label: '打ち切り', badge: BadgeType.CAUTION },
  error: { label: 'エラー', badge: BadgeType.CRITICAL }
}

const formatDetail = (h: BatchHistory) => {
  const d = h.result_detail || {}
  const parts: string[] = []
  if (d.target != null) parts.push(`対象${d.target}`)
  if (d.fetched) parts.push(`取得${d.fetched}`)
  if (d.refetched) parts.push(`再取得${d.refetched}`)
  if (d.failed) parts.push(`失敗${d.failed}`)
  if (d.skipped) parts.push(`見送り${d.skipped}`)
  return parts.length > 0 ? parts.join(' / ') : '-'
}

const formatDuration = (ms: number | null) =>
  ms == null ? '-' : ms >= 1000 ? `${Math.round(ms / 1000)}秒` : `${ms}ms`

interface Props {
  control: Control<SiteFormSchemaType>
  register: UseFormRegister<SiteFormSchemaType>
  // 新規作成時は保存前で siteId が無いため、手動実行と履歴は出さない
  siteId?: number
}

/**
 * 特集ページ画像の取得バッチ設定。
 *
 * V5区分のサイトは W2Commerce がページのコンテンツと画像パスをファイルで持つため
 * マスタ連携に載らず、公開ページから取得しないと記事レコメンドの画像が空のままになる。
 * V6 は連携で届くため、この設定は表示しない（呼び出し側で条件を判定する）。
 */
const SiteFormFeaturePageImage = ({ control, register, siteId }: Props) => {
  const { data: histories, mutate } = useFeaturePageImageHistoriesApi(
    siteId ? { site_id: siteId } : ({} as never)
  )
  const { trigger: runBatch, isMutating } = useFeaturePageImageRunApi(() => mutate())

  return (
    <FormRow>
      <FormLabel>画像保管バッチ</FormLabel>
      <FormRow direction='vertical' gap={8}>
        <FormCheckBox
          control={control}
          name={'featurePageImageBatchEnabled'}
          type={CheckBoxType.TOGGLE_TEXT}
          toggleText={{ left: 'ON', right: 'OFF' }}
        />
        <Text color={TextColor.SECONDARY}>
          特集ページの公開ページから画像を取得して保管します。V5区分のサイトは画像がマスタ連携で
          届かないため、これを有効にしないと記事レコメンドの画像が表示されません。
        </Text>

        <ItemAlign align='center' gapX={8}>
          <Text size={12}>実行時刻</Text>
          <FormSelector
            register={register('featurePageImageBatchHour', registerNumberOption)}
            choices={HOUR_CHOICES}
          />
        </ItemAlign>

        {siteId && (
          <>
            <ItemAlign align='center' gapX={8}>
              <Button
                color={ButtonColor.SECONDARY}
                loading={isMutating}
                disabled={isMutating}
                onClick={() => runBatch({ site_id: siteId })}
              >
                今すぐ実行
              </Button>
              <Text size={12} color={TextColor.SECONDARY}>
                定時実行と同じ処理を行います（未取得のページを優先し、そのあと保管済みを確認）
              </Text>
            </ItemAlign>

            <div>
              <Text size={12} weight='bold'>
                実行履歴（直近10件）
              </Text>
              {!histories || histories.length === 0 ? (
                <Text size={12} color={TextColor.SECONDARY}>
                  まだ実行されていません。
                </Text>
              ) : (
                <div className='mt-1 flex flex-col gap-1'>
                  {histories.map((h) => (
                    <ItemAlign key={h.id} align='center' gapX={8}>
                      <Text size={12}>
                        {h.created_at ? new Date(h.created_at).toLocaleString() : '-'}
                      </Text>
                      <Badge badgeType={BadgeType.SQUARE_TERTIARY} size={12}>
                        {TRIGGER_LABEL[h.trigger] ?? h.trigger}
                      </Badge>
                      <Badge
                        badgeType={STATUS_LABEL[h.status]?.badge ?? BadgeType.DEFAULT}
                        size={12}
                      >
                        {STATUS_LABEL[h.status]?.label ?? h.status}
                      </Badge>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {formatDetail(h)}
                      </Text>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {formatDuration(h.duration_ms)}
                      </Text>
                    </ItemAlign>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </FormRow>
    </FormRow>
  )
}

export default SiteFormFeaturePageImage
